import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { shouldShowList } from '../utils/timeUtils';
import { ListType } from '../constants/listTypes';
import UserAvatar from './UserAvatar';
import MentionAutocomplete from './MentionAutocomplete';
import { linkifyText } from '../utils/textUtils';

function TodoTask({ task, listId, isMainList, isTomorrowList = false, isDailyHabitsList = false, isShared = false, tomorrowCutoffHour = 18, onToggle, onPromote, onDemote, onSnooze, onDelete, onUpdate, onEditingChange, onOpenDrawer, lists = [], expandedSubtasks = false, subtasks = null, onSubtaskToggle }) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(task.text);
  const [isSaving, setIsSaving] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const inputRef = useRef(null);
  const taskRef = useRef(null);
  const prevEditingRef = useRef(false);
  const onEditingChangeRef = useRef(onEditingChange);

  // Minimum swipe distance (in px) to trigger action
  const minSwipeDistance = 80;
  const swipeThreshold = 50;

  // Track window width for desktop detection
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update the ref when callback changes
  useEffect(() => {
    onEditingChangeRef.current = onEditingChange;
  }, [onEditingChange]);

  // Handle focus and select only when entering edit mode, and notify parent
  useEffect(() => {
    const wasEditing = prevEditingRef.current;
    const isNowEditing = isEditing;
    
    // Only select all text when first entering edit mode
    if (isNowEditing && !wasEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
    
    // Only notify parent when editing state actually changes
    if (wasEditing !== isNowEditing && onEditingChangeRef.current) {
      onEditingChangeRef.current(isNowEditing);
    }
    
    prevEditingRef.current = isNowEditing;
  }, [isEditing]);

  const handlePromote = async () => {
    try {
      await onPromote(listId, task.id);
    } catch (error) {
      console.error('Error promoting task:', error);
    }
  };

  const handleDemote = async () => {
    try {
      if (onDemote && task.originalTaskId) {
        const sourceListId = task.sourceList?.id || null;
        await onDemote(listId, task.id, task.originalTaskId, sourceListId);
      }
    } catch (error) {
      console.error('Error demoting task:', error);
    }
  };

  const handleSnooze = async () => {
    try {
      if (onSnooze && task.originalTaskId) {
        const sourceListId = task.sourceList?.id || null;
        await onSnooze(listId, task.id, task.originalTaskId, sourceListId);
      }
    } catch (error) {
      console.error('Error snoozing task:', error);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(listId, task.id);
    }
  };

  const handleTextClick = () => {
    if (task.completed) return;
    
    // Check if it's a desktop viewport (width > 768px)
    // On desktop, open drawer. On mobile, navigate to task detail page.
    if (window.innerWidth > 768) {
      // Desktop: open drawer
      if (onOpenDrawer) {
        onOpenDrawer(task);
      }
    } else {
      // Mobile: navigate to task detail page
      navigate(`/task/${task.id}`);
    }
  };

  const handleSave = async () => {
    const trimmedText = editText.trim();
    
    if (!trimmedText) {
      setEditText(task.text);
      setIsEditing(false);
      return;
    }

    if (trimmedText === task.text) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onUpdate(task.id, trimmedText);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating task:', error);
      setEditText(task.text);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditText(task.text);
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    if (!isSaving) {
      handleSave();
    }
  };

  // Handle mention click - navigate to the list
  const handleMentionClick = (mentionedListId) => {
    // Find the list and navigate to it
    const mentionedList = lists.find(l => l.id === mentionedListId);
    if (mentionedList) {
      navigate(`/list/${mentionedListId}`);
    }
  };

  const onTouchStart = (e) => {
    // Don't handle swipes while editing
    if (isEditing) return;
    
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwiping(true);
  };

  const onTouchMove = (e) => {
    if (!isSwiping || isEditing) return;
    
    const currentTouch = e.targetTouches[0].clientX;
    const diff = currentTouch - touchStart;
    
    // Allow swipes for non-main lists or main/tomorrow list tasks that can be demoted
    if ((!isMainList && !isTomorrowList && !task.completed) || ((isMainList || isTomorrowList) && !task.completed && task.originalTaskId)) {
      // Limit swipe offset to prevent excessive swiping
      const maxOffset = 150;
      const limitedDiff = Math.max(-maxOffset, Math.min(maxOffset, diff));
      setSwipeOffset(limitedDiff);
    }
    
    setTouchEnd(currentTouch);
  };

  const onTouchEnd = async () => {
    if (!touchStart || !touchEnd || isEditing) {
      setSwipeOffset(0);
      setIsSwiping(false);
      return;
    }

    const distance = touchEnd - touchStart;
    const isLeftSwipe = distance < -minSwipeDistance;
    const isRightSwipe = distance > minSwipeDistance;

    // Handle swipe actions for regular user lists (not main/tomorrow) and non-completed tasks
    if (!isMainList && !isTomorrowList && !task.completed && !task.promoted) {
      if (isRightSwipe) {
        // Swipe right to promote
        await handlePromote();
      } else if (isLeftSwipe) {
        // Swipe left to mark as complete
        onToggle(listId, task.id);
      }
    } else if (!isMainList && !isTomorrowList && !task.completed && task.promoted) {
      // If task is promoted, only allow swipe left to complete
      if (isLeftSwipe) {
        onToggle(listId, task.id);
      }
    } else if ((isMainList || isTomorrowList) && !task.completed && task.originalTaskId) {
      // Main/Tomorrow list tasks: swipe right to demote, swipe left to complete
      if (isRightSwipe) {
        // Swipe right to demote
        await handleDemote();
      } else if (isLeftSwipe) {
        // Swipe left to mark as complete
        onToggle(listId, task.id);
      }
    }

    // Reset swipe state
    setSwipeOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
    setIsSwiping(false);
  };

  return (
    <div 
      className="todo-item-wrapper"
      ref={taskRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Swipe action background indicators */}
      <div 
        className="swipe-action-left"
        style={{ 
          opacity: swipeOffset < -swipeThreshold ? 1 : 0,
          transition: isSwiping ? 'none' : 'opacity 0.2s ease'
        }}
      >
        <span className="swipe-icon">✓</span>
      </div>
      <div 
        className="swipe-action-right"
        style={{ 
          opacity: swipeOffset > swipeThreshold ? 1 : 0,
          transition: isSwiping ? 'none' : 'opacity 0.2s ease'
        }}
      >
        <span className="swipe-icon">{(isMainList || isTomorrowList) && task.originalTaskId ? '↓' : '↑'}</span>
      </div>
      
      <div 
        className="todo-item"
        style={{
          transform: `translateX(${swipeOffset}px)`,
          transition: isSwiping ? 'none' : 'transform 0.2s ease'
        }}
      >
      <div
        className="todo-drag-handle"
        aria-label="Reorder task"
        title="Drag to reorder"
      >
        ⋮⋮
      </div>
      <input
        type="checkbox"
        checked={task.completed}
        onChange={() => onToggle(listId, task.id)}
        className="todo-checkbox"
        aria-label={`Mark "${task.text}" as ${task.completed ? 'incomplete' : 'complete'}`}
      />
      <div className="todo-content">
        {isEditing ? (
          <MentionAutocomplete
            inputRef={inputRef}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            lists={lists || []}
            currentListId={listId}
            className="todo-edit-input"
            disabled={isSaving}
          />
        ) : (
          <div className="todo-text-wrapper">
            <div 
              className={`todo-text ${task.completed ? 'todo-text-completed' : ''} ${!task.completed ? 'todo-text-editable' : ''}`}
              onClick={handleTextClick}
            >
              <span>{linkifyText(task.text, lists, handleMentionClick)}</span>
              {/* Show assignee avatar for shared lists (creator is shown in the task drawer) */}
              {isShared && task.assignedTo && task.assignedTo.name && (
                <UserAvatar user={task.assignedTo} size="small" className="todo-assignee-avatar" label="Assigned to" />
              )}
              {/* Show medal emoji for Daily Habit tasks */}
              {task.isDailyHabit ? <span style={{ filter: 'grayscale(100%)', display: 'inline-block' }}> 🏅</span> : ''}
              {task.hasNotes && (
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  className="todo-note-icon"
                  aria-label="Has notes"
                  title="This task has notes"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              )}
              {(isMainList || isTomorrowList) && task.sourceList && (
                <span className="source-badge">
                  {task.sourceList.name}
                </span>
              )}
              {!isMainList && task.promoted && (
                <span className="promoted-badge">
                  {task.promotedToList === 'tomorrow' ? 'To Do Tomorrow' : 'To Do Today'}
                </span>
              )}
            </div>
            {task.subTasksCount > 0 && (
              expandedSubtasks && subtasks && subtasks.length > 0 ? (
                <ul className="todo-subtask-list">
                  {subtasks.map(subTask => (
                    <li key={subTask.id} className="todo-subtask-item">
                      <label className="todo-subtask-checkbox-label">
                        <input
                          type="checkbox"
                          checked={subTask.completed}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            onSubtaskToggle && onSubtaskToggle(task.id, subTask.id, !subTask.completed);
                          }}
                          className="todo-subtask-checkbox"
                        />
                        <span className={subTask.completed ? 'todo-subtask-text-completed' : ''}>
                          {linkifyText(subTask.text, lists, handleMentionClick)}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="todo-subtask-progress">
                  <div
                    className="todo-subtask-progress-bar"
                    style={{
                      width: `${(task.completedSubTasksCount || 0) / task.subTasksCount * 100}%`
                    }}
                  />
                </div>
              )
            )}
          </div>
        )}
      </div>
      {isDailyHabitsList && !task.completed && (
        <div className="todo-actions">
          <button
            onClick={handlePromote}
            className="btn-icon btn-promote"
            aria-label={`Promote "${task.text}" to To Do Today list`}
            title="Promote to To Do Today"
          >
            ↑
          </button>
          <button
            onClick={handleDelete}
            className="btn-icon btn-delete"
            aria-label={`Delete "${task.text}"`}
            title="Delete task"
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" />
            </svg>
          </button>
        </div>
      )}
      {!isMainList && !isTomorrowList && !isDailyHabitsList && !task.promoted && !task.completed && (
        <div className="todo-actions">
          <button
            onClick={handlePromote}
            className="btn-icon btn-promote"
            aria-label={`Promote "${task.text}" to To Do Today list`}
            title="Promote to To Do Today"
          >
            ↑
          </button>
        </div>
      )}
      {(isMainList || isTomorrowList) && !task.completed && task.originalTaskId && (
        <div className="todo-actions">
          {isMainList && isDesktop && shouldShowList(ListType.TOMORROW, tomorrowCutoffHour) && (
            <button
              onClick={handleSnooze}
              className="btn-icon btn-promote"
              aria-label={`Snooze "${task.text}" to To Do Tomorrow list`}
              title="Snooze to To Do Tomorrow"
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </button>
          )}
          <button
            onClick={handleDemote}
            className="btn-icon btn-promote"
            aria-label={`Remove "${task.text}" from ${isTomorrowList ? 'To Do Tomorrow' : 'To Do Today'} list`}
            title={`Remove from ${isTomorrowList ? 'To Do Tomorrow' : 'To Do Today'}`}
          >
            ↓
          </button>
        </div>
      )}
    </div>
    </div>
  );
}

// Memoize component to prevent unnecessary re-renders
// Only re-render if task data or critical props change
export default React.memo(TodoTask, (prevProps, nextProps) => {
  // Re-render if task properties changed
  if (prevProps.task.id !== nextProps.task.id) return false;
  if (prevProps.task.text !== nextProps.task.text) return false;
  if (prevProps.task.completed !== nextProps.task.completed) return false;
  if (prevProps.task.promoted !== nextProps.task.promoted) return false;
  if (prevProps.task.hasNotes !== nextProps.task.hasNotes) return false;
  if (prevProps.task.subTasksCount !== nextProps.task.subTasksCount) return false;
  if (prevProps.task.completedSubTasksCount !== nextProps.task.completedSubTasksCount) return false;

  // Re-render if expanded sub-task display or its data changed
  if (prevProps.expandedSubtasks !== nextProps.expandedSubtasks) return false;
  if (prevProps.subtasks !== nextProps.subtasks) return false;

  // Re-render if list context changed
  if (prevProps.listId !== nextProps.listId) return false;
  if (prevProps.isMainList !== nextProps.isMainList) return false;
  if (prevProps.isTomorrowList !== nextProps.isTomorrowList) return false;
  if (prevProps.isDailyHabitsList !== nextProps.isDailyHabitsList) return false;
  if (prevProps.isShared !== nextProps.isShared) return false;

  // Re-render if assignee changed (for shared lists)
  if (prevProps.task.assignedTo?.userId !== nextProps.task.assignedTo?.userId) return false;
  if (prevProps.task.assignedTo?.name !== nextProps.task.assignedTo?.name) return false;

  // Don't re-render otherwise (callbacks are assumed stable)
  return true;
});

